import React, { useState, useEffect } from 'react';
import { DollarSign, Clock, CheckCircle, Filter, Users, ArrowDownCircle, Wallet, Timer, Search, Video } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../../components/SharedUI';
import { 
  getTmEarnings, 
  getTmEarningsSummary, 
  markTmEarningPaid, 
  markTmEarningsPaidBulk, 
  formatIQD, 
  formatSeconds, 
  getTmTeamPayments, 
  getTmTeamDeductions, 
  getTmSecondsSummary, 
  getTmMyDebts,
  getTmLeadersDues,
  payAllLeaderDues,
  getTmDeliveriesReport,
  adjustTmEarning,
  getTmMyReceivedPayments
} from '../../utils/tm_api';
import { getEmployees } from '../../utils/api';

export default function TmEarningsPage() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isOwner = ['owner', 'admin'].includes(user.role);
  const isMember = user.role === 'team_member';
  const isTeamLeader = user.role === 'team_leader';
  const isSupervisor = user.role === 'supervisor';
  const showFinancials = !isMember || user.show_financials === 1 || user.show_financials === true || user.show_financials === '1' || localStorage.getItem('show_financials') === 'true';

  const [earnings, setEarnings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selected, setSelected] = useState([]);
  
  // Set default tab dynamically based on role
  const [tab, setTab] = useState(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isMember = user.role === 'team_member';
    const showFinancials = !isMember || user.show_financials === 1 || user.show_financials === true || user.show_financials === '1' || localStorage.getItem('show_financials') === 'true';
    if (['owner', 'admin'].includes(user.role)) return 'leaders-dues';
    if (isMember && !showFinancials) return 'seconds';
    return 'earnings';
  });

  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [receivedPayments, setReceivedPayments] = useState([]);
  const [loadingReceived, setLoadingReceived] = useState(false);
  const [deductions, setDeductions] = useState([]);
  const [loadingDeductions, setLoadingDeductions] = useState(false);
  const [secondsSummary, setSecondsSummary] = useState(null);
  const [loadingSeconds, setLoadingSeconds] = useState(false);
  const [dayFilter, setDayFilter] = useState('');
  
  const [myDebts, setMyDebts] = useState([]);
  const [loadingDebts, setLoadingDebts] = useState(false);

  // ═══ NEW FEATURES STATE ═══
  const [leadersDues, setLeadersDues] = useState([]);
  const [loadingLeadersDues, setLoadingLeadersDues] = useState(false);
  const [leadersSearch, setLeadersSearch] = useState('');
  const [showUnpaidOnly, setShowUnpaidOnly] = useState(false);
  const [deliveriesReport, setDeliveriesReport] = useState([]);
  const [loadingDeliveriesReport, setLoadingDeliveriesReport] = useState(false);
  const [deliveriesSearch, setDeliveriesSearch] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedLeaderId, setSelectedLeaderId] = useState('');

  // ═══ DEDUCTION / ADJUSTMENT STATE ═══
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [adjustEmpId, setAdjustEmpId] = useState('');
  const [adjustType, setAdjustType] = useState('deduction');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustMonth, setAdjustMonth] = useState(month);
  const [adjustNotes, setAdjustNotes] = useState('');
  const [submittingAdjust, setSubmittingAdjust] = useState(false);

  const [earningsSearch, setEarningsSearch] = useState('');
  const [earningsFilter, setEarningsFilter] = useState('all'); // 'all', 'tasks', 'adjustments'

  const filteredEarnings = earnings.filter(e => {
    const matchesSearch = 
      (e.employee_name || '').toLowerCase().includes(earningsSearch.toLowerCase()) ||
      (e.task_title || '').toLowerCase().includes(earningsSearch.toLowerCase()) ||
      (e.notes || '').toLowerCase().includes(earningsSearch.toLowerCase());
    
    if (!matchesSearch) return false;

    if (earningsFilter === 'tasks') return e.task_id !== null;
    if (earningsFilter === 'adjustments') return e.task_id === null;
    
    return true;
  });

  useEffect(() => {
    setAdjustMonth(month);
  }, [month]);

  const loadEmployeesList = async () => {
    try {
      setLoadingEmployees(true);
      const data = await getEmployees();
      // Filter active employees
      setEmployees(data || []);
    } catch (err) {
      console.error(err);
      toast.error('خطأ في تحميل قائمة الموظفين');
    } finally {
      setLoadingEmployees(false);
    }
  };

  useEffect(() => {
    if (showAdjustModal && employees.length === 0) {
      loadEmployeesList();
    }
  }, [showAdjustModal]);

  const load = async () => {
    try {
      setLoading(true);
      const [e, s] = await Promise.all([
        getTmEarnings({ month }),
        isOwner ? getTmEarningsSummary({ month }) : null
      ]);
      setEarnings(e);
      if (s) setSummary(s);

      if ((isMember || isTeamLeader) && !isSupervisor) {
        getTmTeamPayments(month).then(res => setPayments(res || [])).catch(() => {});
        getTmTeamDeductions(month).then(res => setDeductions(res || [])).catch(() => {});
        if (isTeamLeader) {
          setLoadingReceived(true);
          getTmMyReceivedPayments({ month })
            .then(res => setReceivedPayments(res || []))
            .catch(() => {})
            .finally(() => setLoadingReceived(false));
        }
      }
    } catch (e) { toast.error('خطأ في التحميل'); }
    finally { setLoading(false); }
  };

  const loadLeadersDues = async () => {
    if (!isOwner) return;
    try {
      setLoadingLeadersDues(true);
      const data = await getTmLeadersDues({ month });
      setLeadersDues(data || []);
    } catch (err) {
      console.error(err);
      toast.error('خطأ في تحميل مستحقات قادة الفرق');
    } finally {
      setLoadingLeadersDues(false);
    }
  };

  const loadDeliveriesReport = async () => {
    if (!isOwner && !isSupervisor && !isTeamLeader) return;
    try {
      setLoadingDeliveriesReport(true);
      const params = { month };
      if (selectedTeamId) params.team_id = selectedTeamId;
      if (selectedLeaderId) params.leader_employee_id = selectedLeaderId;
      const data = await getTmDeliveriesReport(params);
      setDeliveriesReport(data || []);
    } catch (err) {
      console.error(err);
      toast.error('خطأ في تحميل تقرير التسليمات');
    } finally {
      setLoadingDeliveriesReport(false);
    }
  };

  const handlePayAllLeaderDues = async (leaderId, leaderName) => {
    if (!window.confirm(`هل أنت متأكد من دفع كافة المستحقات للقائد ${leaderName}؟`)) return;
    try {
      const res = await payAllLeaderDues({ employee_id: leaderId, month });
      toast.success(res.message || 'تم تسجيل الدفع بنجاح');
      loadLeadersDues();
      load(); // Reload detailed list and stats
    } catch (err) {
      toast.error(err?.response?.data?.error || 'خطأ في عملية الدفع');
    }
  };

  const handleAdjustSubmit = async (e) => {
    e.preventDefault();
    if (!adjustEmpId || !adjustAmount || !adjustMonth || !adjustNotes) {
      toast.error('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    const rawVal = parseFloat(adjustAmount);
    if (isNaN(rawVal) || rawVal <= 0) {
      toast.error('يرجى إدخال مبلغ صالح أكبر من الصفر');
      return;
    }

    // Deduction converts amount to negative
    const finalAmount = adjustType === 'deduction' ? -rawVal : rawVal;

    try {
      setSubmittingAdjust(true);
      await adjustTmEarning({
        employee_id: parseInt(adjustEmpId),
        amount_iqd: finalAmount,
        period_month: adjustMonth,
        notes: adjustNotes
      });
      toast.success('تم تسجيل التسوية المالية بنجاح');
      setShowAdjustModal(false);
      setAdjustEmpId('');
      setAdjustAmount('');
      setAdjustNotes('');
      load();
      if (isOwner) loadLeadersDues();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'فشل إدخال التسوية');
    } finally {
      setSubmittingAdjust(false);
    }
  };

  const loadSeconds = async () => {
    if (!isMember && !isTeamLeader) return;
    try {
      setLoadingSeconds(true);
      const params = { month };
      if (dayFilter) params.day = dayFilter;
      const data = await getTmSecondsSummary(params);
      setSecondsSummary(data);
    } catch (e) { console.error('seconds error', e); }
    finally { setLoadingSeconds(false); }
  };

  const loadDebts = async () => {
    if (!isMember && !isTeamLeader) return;
    try {
      setLoadingDebts(true);
      const res = await getTmMyDebts();
      if (res.success) setMyDebts(res.data);
    } catch (e) { console.error('debts error', e); }
    finally { setLoadingDebts(false); }
  };

  useEffect(() => {
    load();
    if (isOwner) {
      loadLeadersDues();
    }
    if (isOwner || isSupervisor || isTeamLeader) {
      loadDeliveriesReport();
    }
  }, [month]);

  useEffect(() => {
    if (tab === 'seconds') loadSeconds();
  }, [month, dayFilter, tab]);

  useEffect(() => {
    if (tab === 'debts') loadDebts();
  }, [tab]);

  useEffect(() => {
    if (tab === 'leaders-dues' && isOwner) {
      loadLeadersDues();
    }
    if (tab === 'deliveries-report' && (isOwner || isSupervisor || isTeamLeader)) {
      loadDeliveriesReport();
    }
  }, [tab, selectedTeamId, selectedLeaderId]);

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const selectAll = () => {
    const unpaidIds = filteredEarnings.filter(e => !e.is_paid).map(e => e.id);
    setSelected(s => s.length === unpaidIds.length ? [] : unpaidIds);
  };

  const handleBulkPay = async () => {
    if (selected.length === 0) return;
    try {
      await markTmEarningsPaidBulk(selected);
      toast.success(`تم دفع ${selected.length} مستحق`);
      setSelected([]);
      load();
      if (isOwner) loadLeadersDues();
    } catch (e) { toast.error('خطأ'); }
  };

  const roleLabel = (r) => r === 'team_leader' ? 'قائد فريق' : r === 'team_member' ? 'عضو' : r;

  // حساب إجمالي مستحقات الشهر للعضو
  const memberTotal = isMember ? earnings.reduce((sum, e) => sum + (e.total_earned_iqd || 0), 0) : 0;
  const memberPaid = isMember ? payments.reduce((sum, p) => sum + (p.amount_iqd || 0), 0) : 0;
  const totalDeductions = isMember ? deductions.reduce((sum, d) => sum + (d.amount_iqd || 0), 0) : 0;
  const memberUnpaid = memberTotal - totalDeductions - memberPaid;

  // حساب إجمالي مستحقات الشهر لقائد الفريق (أرباحه الشخصية من الإدارة)
  const leaderTotal = isTeamLeader ? earnings.filter(e => e.role_type === 'team_leader' && e.employee_id === user.employee_id).reduce((sum, e) => sum + (e.total_earned_iqd || 0), 0) : 0;
  const leaderPaid = isTeamLeader ? earnings.filter(e => e.role_type === 'team_leader' && e.employee_id === user.employee_id && e.is_paid === 1).reduce((sum, e) => sum + (e.total_earned_iqd || 0), 0) : 0;
  const leaderUnpaid = leaderTotal - leaderPaid;
  const myPersonalAdjustments = earnings.filter(e => e.employee_id === user.employee_id && e.task_id === null);

  const totalUnpaidLeadersDues = leadersDues.reduce((sum, item) => sum + (item.unpaid_iqd || 0), 0);

  // تصفية مستحقات قادة الفرق محلياً
  const filteredLeadersDues = leadersDues.filter(leader => {
    const matchesSearch = leader.employee_name?.toLowerCase().includes(leadersSearch.toLowerCase());
    const matchesUnpaid = !showUnpaidOnly || leader.unpaid_iqd > 0;
    return matchesSearch && matchesUnpaid;
  });

  // تصفية تقرير الفيديوهات والتسليمات محلياً للسرعة القصوى والسلاسة
  const filteredDeliveries = deliveriesReport.filter(d => {
    const search = deliveriesSearch.toLowerCase();
    return (
      d.task_title?.toLowerCase().includes(search) ||
      d.team_name?.toLowerCase().includes(search) ||
      d.leader_name?.toLowerCase().includes(search)
    );
  });

  const totalReportVideos = filteredDeliveries.length;
  const totalReportSeconds = filteredDeliveries.reduce((sum, item) => sum + (item.seconds || 0), 0);

  return (
    <div className="p-4 md:p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--text-dark-h)]">
              {isMember ? 'مستحقاتي' : isSupervisor ? 'عمولاتي' : 'المستحقات والأرباح'}
            </h1>
            <p className="text-sm text-[var(--text-muted)]">
              {tab === 'leaders-dues' ? `${filteredLeadersDues.length} قائد فريق` : 
               tab === 'deliveries-report' ? `${filteredDeliveries.length} فيديو مسلّم` : 
               `${earnings.length} سجل مستحقات`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isOwner && (
            <button onClick={() => setShowAdjustModal(true)}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-sm transition shadow-sm flex items-center gap-1">
              إضافة تسوية / خصم ➕
            </button>
          )}
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            className="p-2 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm" />
          {isOwner && tab === 'earnings' && selected.length > 0 && (
            <button onClick={handleBulkPay}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-sm transition">
              <CheckCircle className="w-4 h-4 inline ml-1" /> دفع {selected.length}
            </button>
          )}
        </div>
      </div>

      {/* Role-Based Universal Tabs */}
      <div className="flex gap-2 border-b border-[var(--border-light)] pb-2 overflow-x-auto scrollbar-none">
        {isOwner && (
          <>
            <button onClick={() => setTab('leaders-dues')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition whitespace-nowrap ${tab === 'leaders-dues' ? 'bg-green-600 text-white shadow-sm' : 'bg-[var(--bg-flat-50)] text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'}`}>
              👥 مستحقات قادة الفرق مجتمعة
            </button>
            <button onClick={() => setTab('earnings')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition whitespace-nowrap ${tab === 'earnings' ? 'bg-green-600 text-white shadow-sm' : 'bg-[var(--bg-flat-50)] text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'}`}>
              📋 السجل التفصيلي للمستحقات
            </button>
            <button onClick={() => setTab('deliveries-report')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition whitespace-nowrap ${tab === 'deliveries-report' ? 'bg-indigo-600 text-white shadow-sm' : 'bg-[var(--bg-flat-50)] text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'}`}>
              🎬 تقرير الفيديوهات والتسليم
            </button>
          </>
        )}

        {isSupervisor && (
          <>
            <button onClick={() => setTab('earnings')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition whitespace-nowrap ${tab === 'earnings' ? 'bg-green-600 text-white shadow-sm' : 'bg-[var(--bg-flat-50)] text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'}`}>
              💰 عمولاتي المباشرة
            </button>
            <button onClick={() => setTab('deliveries-report')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition whitespace-nowrap ${tab === 'deliveries-report' ? 'bg-indigo-600 text-white shadow-sm' : 'bg-[var(--bg-flat-50)] text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'}`}>
              🎬 تقرير الفيديوهات والتسليم
            </button>
          </>
        )}

        {isTeamLeader && (
          <>
            <button onClick={() => setTab('earnings')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition whitespace-nowrap ${tab === 'earnings' ? 'bg-green-600 text-white shadow-sm' : 'bg-[var(--bg-flat-50)] text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'}`}>
              💵 أرباح الأعضاء
            </button>
            <button onClick={() => setTab('payments')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition whitespace-nowrap ${tab === 'payments' ? 'bg-green-600 text-white shadow-sm' : 'bg-[var(--bg-flat-50)] text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'}`}>
              💳 سجل الدفعات
            </button>
            <button onClick={() => setTab('deductions')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition whitespace-nowrap ${tab === 'deductions' ? 'bg-red-600 text-white shadow-sm' : 'bg-[var(--bg-flat-50)] text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'}`}>
              ⛔ الخصومات
            </button>
            <button onClick={() => setTab('seconds')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition whitespace-nowrap ${tab === 'seconds' ? 'bg-cyan-600 text-white shadow-sm' : 'bg-[var(--bg-flat-50)] text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'}`}>
              ⏱️ الثواني المنجزة
            </button>
            <button onClick={() => setTab('deliveries-report')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition whitespace-nowrap ${tab === 'deliveries-report' ? 'bg-indigo-600 text-white shadow-sm' : 'bg-[var(--bg-flat-50)] text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'}`}>
              🎬 تقرير الفيديوهات والتسليم
            </button>
            <button onClick={() => setTab('debts')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition whitespace-nowrap ${tab === 'debts' ? 'bg-orange-600 text-white shadow-sm' : 'bg-[var(--bg-flat-50)] text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'}`}>
              💸 الديون والسلف
            </button>
          </>
        )}

        {isMember && (
          <>
            {showFinancials && (
              <>
                <button onClick={() => setTab('earnings')}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition whitespace-nowrap ${tab === 'earnings' ? 'bg-green-600 text-white shadow-sm' : 'bg-[var(--bg-flat-50)] text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'}`}>
                  💵 مستحقاتي
                </button>
                <button onClick={() => setTab('payments')}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition whitespace-nowrap ${tab === 'payments' ? 'bg-green-600 text-white shadow-sm' : 'bg-[var(--bg-flat-50)] text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'}`}>
                  💳 الدفعات المستلمة
                </button>
                <button onClick={() => setTab('deductions')}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition whitespace-nowrap ${tab === 'deductions' ? 'bg-red-600 text-white shadow-sm' : 'bg-[var(--bg-flat-50)] text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'}`}>
                  ⛔ الخصومات
                </button>
              </>
            )}
            <button onClick={() => setTab('seconds')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition whitespace-nowrap ${tab === 'seconds' ? 'bg-cyan-600 text-white shadow-sm' : 'bg-[var(--bg-flat-50)] text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'}`}>
              ⏱️ الثواني المنجزة
            </button>
            {showFinancials && (
              <button onClick={() => setTab('debts')}
                className={`px-4 py-2 rounded-xl text-sm font-bold transition whitespace-nowrap ${tab === 'debts' ? 'bg-orange-600 text-white shadow-sm' : 'bg-[var(--bg-flat-50)] text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'}`}>
                💸 الديون والسلف
              </button>
            )}
          </>
        )}
      </div>

      {/* Member Summary Card (Member only under earnings tab) */}
      {isMember && tab === 'earnings' && showFinancials && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-2xl bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400">
            <p className="text-xs font-bold opacity-60 mb-1">إجمالي الشهر</p>
            <p className="text-lg font-bold">{formatIQD(memberTotal)}</p>
          </div>
          <div className="p-4 rounded-2xl bg-red-50 dark:bg-red-900/20 text-red-650 dark:text-red-450">
            <p className="text-xs font-bold opacity-60 mb-1">إجمالي الخصومات</p>
            <p className="text-lg font-bold">-{formatIQD(totalDeductions)}</p>
          </div>
          <div className="p-4 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
            <p className="text-xs font-bold opacity-60 mb-1">مدفوع فعلياً (سند صرف)</p>
            <p className="text-lg font-bold">{formatIQD(memberPaid)}</p>
          </div>
          <div className="p-4 rounded-2xl bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400">
            <p className="text-xs font-bold opacity-60 mb-1">المتبقي (الصافي)</p>
            <p className="text-lg font-bold">{formatIQD(memberUnpaid)}</p>
          </div>
        </div>
      )}

      {/* Team Leader Summary Card (Leader only under earnings and payments tabs) */}
      {isTeamLeader && ['earnings', 'payments'].includes(tab) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-2xl bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400">
            <p className="text-xs font-bold opacity-60 mb-1">إجمالي مستحقات قائد الفريق</p>
            <p className="text-lg font-bold">{formatIQD(leaderTotal)}</p>
          </div>
          <div className="p-4 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
            <p className="text-xs font-bold opacity-60 mb-1">مدفوع فعلياً (سند صرف)</p>
            <p className="text-lg font-bold">{formatIQD(leaderPaid)}</p>
          </div>
          <div className="p-4 rounded-2xl bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400">
            <p className="text-xs font-bold opacity-60 mb-1">المتبقي (الصافي)</p>
            <p className="text-lg font-bold">{formatIQD(leaderUnpaid)}</p>
          </div>
        </div>
      )}

      {/* Owner Summary Cards (Shown on Leaders Dues or Detailed list) */}
      {isOwner && summary && ['leaders-dues', 'earnings'].includes(tab) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard title="إجمالي الأرباح" value={formatIQD(summary.total_earned_iqd)} color="green" icon={<DollarSign className="w-5 h-5" />} />
          <SummaryCard title="الثواني المقبولة" value={formatSeconds(summary.total_seconds)} color="blue" icon={<Clock className="w-5 h-5" />} />
          <SummaryCard title="عمولات المتابعين" value={formatIQD(summary.supervisor_commissions?.total_iqd)} color="purple" icon={<Users className="w-5 h-5" />} />
          <SummaryCard title="غير مدفوع" value={formatIQD(summary.total_unpaid_iqd)} color="red" icon={<ArrowDownCircle className="w-5 h-5" />} />
        </div>
      )}

      {/* ═══ VIEW: Grouped Team Leaders Dues (Owner / Admin only) ═══ */}
      {tab === 'leaders-dues' && isOwner && (
        <div className="space-y-6">
          {/* Top Unpaid Summary Card */}
          <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/40 rounded-2xl p-5 flex flex-col md:flex-row items-center justify-between gap-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center shadow-inner">
                <Wallet className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="text-right">
                <h3 className="text-sm font-bold text-red-800 dark:text-red-300">إجمالي مستحقات قادة الفرق المعلقة (غير مدفوعة)</h3>
                <p className="text-2xl font-black text-red-600 dark:text-red-400 mt-1">{formatIQD(totalUnpaidLeadersDues)}</p>
              </div>
            </div>
            <div className="px-3.5 py-1.5 rounded-xl bg-red-100/50 dark:bg-red-900/20 text-xs font-bold text-red-700 dark:text-red-300 border border-red-200/50 dark:border-red-900/20">
              الفترة: {month}
            </div>
          </div>

          {/* Leaders Dues Grouped Table */}
          {loadingLeadersDues ? (
            <div className="text-center py-12 text-[var(--text-muted)]">جاري تحميل مستحقات قادة الفرق...</div>
          ) : (
            <div className="space-y-4">
              {/* Filter controls */}
              <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 flex flex-col md:flex-row items-center justify-between gap-4 shadow-sm">
                <div className="relative w-full md:max-w-xs">
                  <input
                    type="text"
                    value={leadersSearch}
                    onChange={e => setLeadersSearch(e.target.value)}
                    placeholder="بحث باسم قائد الفريق..."
                    className="w-full p-2.5 pl-8 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 transition"
                  />
                  <Search className="w-4 h-4 text-[var(--text-gray)] absolute left-3 top-3.5" />
                </div>
                
                <div className="flex items-center gap-2 self-start md:self-center">
                  <input
                    type="checkbox"
                    id="unpaidOnly"
                    checked={showUnpaidOnly}
                    onChange={e => setShowUnpaidOnly(e.target.checked)}
                    className="w-4 h-4 accent-red-600 rounded cursor-pointer"
                  />
                  <label htmlFor="unpaidOnly" className="text-xs font-bold text-[var(--text-dark-s)] cursor-pointer select-none">
                    ⚠️ عرض المعلقة (غير المدفوعة) فقط
                  </label>
                </div>
              </div>

              {filteredLeadersDues.length === 0 ? (
                <div className="text-center py-12 text-[var(--text-muted)] bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl">
                  لا توجد مستحقات لقادة الفرق تطابق خيارات البحث والفلترة لشهر {month}
                </div>
              ) : (
                <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-sm">
                  <div className="px-4 py-3 bg-[var(--bg-flat-50)] border-b border-[var(--border-light)] flex justify-between items-center">
                    <h3 className="text-sm font-bold text-[var(--text-dark-h)] flex items-center gap-2">
                      <span>👥 مستحقات قادة الفرق مجتمعة</span>
                    </h3>
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400">
                      {filteredLeadersDues.length} قائد فريق
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[var(--bg-flat-50)] border-b border-[var(--border-light)]">
                          <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">القائد</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">الشهر</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">الثواني المقبولة</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المهام المنجزة</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">إجمالي المستحقات</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المدفوع</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المتبقي (غير المدفوع)</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-[var(--text-gray)]">الإجراءات</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLeadersDues.map((leader) => (
                          <tr key={`${leader.employee_id}-${leader.period_month}`} className="border-t border-[var(--border-light)] hover:bg-[var(--table-row-hover)] transition">
                            <td className="px-4 py-3.5 font-bold text-[var(--text-dark-h)]">{leader.employee_name}</td>
                            <td className="px-4 py-3.5 text-[var(--text-dark-s)]">{leader.period_month}</td>
                            <td className="px-4 py-3.5 font-semibold text-cyan-600 dark:text-cyan-400">{formatSeconds(leader.total_seconds)}</td>
                            <td className="px-4 py-3.5 text-[var(--text-dark-s)]">{leader.task_count} مهمة</td>
                            <td className="px-4 py-3.5 font-bold text-green-600 dark:text-green-400">{formatIQD(leader.total_earned_iqd)}</td>
                            <td className="px-4 py-3.5 font-semibold text-blue-600 dark:text-blue-400">{formatIQD(leader.paid_iqd)}</td>
                            <td className="px-4 py-3.5 font-bold text-red-600 dark:text-red-400">{formatIQD(leader.unpaid_iqd)}</td>
                            <td className="px-4 py-3.5 text-center">
                              {leader.unpaid_iqd > 0 ? (
                                <button
                                  onClick={() => handlePayAllLeaderDues(leader.employee_id, leader.employee_name)}
                                  className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold shadow-sm hover:shadow transition-all inline-flex items-center gap-1.5"
                                >
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  دفع كافة المستحقات
                                </button>
                              ) : (
                                <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 inline-flex items-center gap-1">
                                  ✅ مدفوع بالكامل
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══ VIEW: Videos and Deliveries Report Tab (Owner / Admin / Supervisor / Team Leader) ═══ */}
      {tab === 'deliveries-report' && (isOwner || isSupervisor || isTeamLeader) && (
        <div className="space-y-6">
          {/* Controls & Summary stats */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5 space-y-4 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[var(--border-light)] pb-4">
              <div className="flex items-center gap-2">
                <Video className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                <h3 className="text-sm font-bold text-[var(--text-dark-h)]">فلترة وإحصائيات تقرير الفيديوهات والتسليم</h3>
              </div>
              
              <div className="flex gap-4">
                <div className="px-4 py-2 bg-indigo-50 dark:bg-indigo-950/20 text-indigo-700 dark:text-indigo-300 rounded-xl text-center border border-indigo-100 dark:border-indigo-900/30 shadow-inner">
                  <p className="text-[10px] font-bold opacity-60">إجمالي الفيديوهات</p>
                  <p className="text-lg font-black">{totalReportVideos} فيديو</p>
                </div>
                <div className="px-4 py-2 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300 rounded-xl text-center border border-cyan-100 dark:border-cyan-900/30 shadow-inner">
                  <p className="text-[10px] font-bold opacity-60">إجمالي مدة الفيديوهات</p>
                  <p className="text-lg font-black">{formatSeconds(totalReportSeconds)}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search input */}
              <div>
                <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">بحث تفاعلي</label>
                <div className="relative">
                  <input
                    type="text"
                    value={deliveriesSearch}
                    onChange={e => setDeliveriesSearch(e.target.value)}
                    placeholder="ابحث بالعنوان، الفريق، أو القائد..."
                    className="w-full p-2.5 pl-8 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition"
                  />
                  <Search className="w-4 h-4 text-[var(--text-gray)] absolute left-3 top-3.5" />
                </div>
              </div>

              {/* Dynamic Dropdown: Team name filter (Owner/Supervisor only) */}
              {(isOwner || isSupervisor) && (
                <>
                  <div>
                    <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">تصفية حسب الفريق</label>
                    <select
                      value={selectedTeamId}
                      onChange={e => setSelectedTeamId(e.target.value)}
                      className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition"
                    >
                      <option value="">كل الفرق</option>
                      {Array.from(new Set(deliveriesReport.map(item => item.team_name))).filter(Boolean).map(teamName => (
                        <option key={teamName} value={teamName}>{teamName}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">تصفية حسب القائد</label>
                    <select
                      value={selectedLeaderId}
                      onChange={e => setSelectedLeaderId(e.target.value)}
                      className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition"
                    >
                      <option value="">كل القادة</option>
                      {Array.from(new Map(deliveriesReport.map(item => [item.leader_employee_id, item.leader_name])).entries()).filter(([id]) => id).map(([id, name]) => (
                        <option key={id} value={id}>{name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Deliveries detailed table */}
          {loadingDeliveriesReport ? (
            <div className="text-center py-12 text-[var(--text-muted)]">جاري تحميل تقرير التسليمات...</div>
          ) : filteredDeliveries.length === 0 ? (
            <div className="text-center py-12 text-[var(--text-muted)] bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl">
              لا توجد فيديوهات أو تسليمات تطابق الفلترة والبحث لشهر {month}
            </div>
          ) : (
            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[var(--bg-flat-50)] border-b border-[var(--border-light)]">
                      <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">تاريخ التسليم</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">عنوان الفيديو / المهمة</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">الفريق</th>
                      {(isOwner || isSupervisor) && <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">القائد المسؤول</th>}
                      <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">مدة الفيديو (ثانية)</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المعامل</th>
                      {!isSupervisor && <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المبلغ المستحق</th>}
                      {!isSupervisor && <th className="px-4 py-3 text-center text-xs font-bold text-[var(--text-gray)]">الحالة</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDeliveries.map((item, idx) => (
                      <tr key={idx} className="border-t border-[var(--border-light)] hover:bg-[var(--table-row-hover)] transition">
                        <td className="px-4 py-3.5 text-[var(--text-dark-s)] text-xs">
                          {item.delivery_date ? new Date(item.delivery_date).toLocaleDateString('ar-IQ') : '—'}
                        </td>
                        <td className="px-4 py-3.5 font-bold text-[var(--text-dark-h)]">{item.task_title}</td>
                        <td className="px-4 py-3.5 text-[var(--text-dark-s)]">{item.team_name}</td>
                        {(isOwner || isSupervisor) && <td className="px-4 py-3.5 font-semibold text-[var(--text-dark-h)]">{item.leader_name}</td>}
                        <td className="px-4 py-3.5 font-bold text-cyan-600 dark:text-cyan-400">{item.seconds} ثانية</td>
                        <td className="px-4 py-3.5 text-[var(--text-dark-s)]">✖️{item.multiplier || 1}</td>
                        {!isSupervisor && <td className="px-4 py-3.5 font-bold text-green-600 dark:text-green-400">{formatIQD(item.total_earned_iqd)}</td>}
                        {!isSupervisor && (
                          <td className="px-4 py-3.5 text-center">
                            {item.is_paid ? (
                              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 inline-flex items-center gap-1">
                                مدفوع ✅
                              </span>
                            ) : (
                              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 inline-flex items-center gap-1">
                                غير مدفوع
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ VIEW: Detailed Earnings List ═══ */}
      {tab === 'earnings' && showFinancials && (
        <>
          {loading ? (
            <div className="text-center py-12 text-[var(--text-muted)]">جاري التحميل...</div>
          ) : earnings.length === 0 ? (
            <div className="text-center py-12 text-[var(--text-muted)] bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl">
              لا توجد مستحقات لهذا الشهر
            </div>
          ) : (
            <>
              {/* Detailed Earning Filters */}
              <div className="mb-4 flex flex-col sm:flex-row gap-3 items-center justify-between">
                <div className="relative w-full sm:w-80">
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <Search className="h-4 w-4 text-[var(--text-muted)]" />
                  </span>
                  <input
                    type="text"
                    placeholder="ابحث باسم الموظف، المهمة أو التسوية..."
                    value={earningsSearch}
                    onChange={e => setEarningsSearch(e.target.value)}
                    className="w-full pl-3 pr-9 py-2 text-sm bg-[var(--input-bg)] border border-[var(--border-light)] rounded-xl text-[var(--text-dark-h)] focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                </div>

                <div className="flex items-center gap-2 bg-[var(--bg-flat-50)] rounded-xl border border-[var(--border-light)] px-3 py-1.5 w-full sm:w-auto">
                  <Filter className="w-4 h-4 text-[var(--text-gray)]" />
                  <select
                    value={earningsFilter}
                    onChange={e => setEarningsFilter(e.target.value)}
                    className="bg-transparent border-0 text-xs font-bold text-[var(--text-dark-h)] focus:ring-0 outline-none cursor-pointer pr-6 py-0.5"
                  >
                    <option value="all" className="bg-[var(--card-bg)] text-[var(--text-dark-h)]">جميع القيود</option>
                    <option value="tasks" className="bg-[var(--card-bg)] text-[var(--text-dark-h)]">المهام فقط 🎬</option>
                    <option value="adjustments" className="bg-[var(--card-bg)] text-[var(--text-dark-h)]">الخصومات والتسويات فقط ⚠️</option>
                  </select>
                </div>
              </div>

              <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[var(--bg-flat-50)] border-b border-[var(--border-light)]">
                        {isOwner && (
                          <th className="px-3 py-3 text-right">
                            <input type="checkbox" onChange={selectAll} checked={selected.length > 0 && selected.length === filteredEarnings.filter(e => !e.is_paid).length} className="accent-primary" />
                          </th>
                        )}
                        {!isMember && !isSupervisor && <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">الموظف</th>}
                        {!isMember && !isSupervisor && <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">الدور</th>}
                        <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المهمة / التسوية</th>
                        {!isMember && !isSupervisor && <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">الفريق</th>}
                        <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">الثواني</th>
                        <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المعامل</th>
                        <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المبلغ</th>
                        <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">الحالة</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEarnings.length === 0 ? (
                        <tr>
                          <td colSpan={isOwner ? 9 : 8} className="text-center py-12 text-[var(--text-muted)] font-medium">
                            🔍 لا توجد مستحقات أو تسويات تطابق البحث والتصفية الحاليين
                          </td>
                        </tr>
                      ) : (
                        filteredEarnings.map(e => (
                          <tr key={e.id} className="border-t border-[var(--border-light)] hover:bg-[var(--table-row-hover)] transition">
                            {isOwner && (
                              <td className="px-3 py-3">
                                {!e.is_paid && <input type="checkbox" checked={selected.includes(e.id)} onChange={() => toggleSelect(e.id)} className="accent-primary" />}
                              </td>
                            )}
                            {!isMember && !isSupervisor && <td className="px-3 py-3 font-bold text-[var(--text-dark-h)]">{e.employee_name}</td>}
                            {!isMember && !isSupervisor && <td className="px-3 py-3 text-[var(--text-gray)]">{roleLabel(e.role_type)}</td>}
                            <td className="px-3 py-3 text-[var(--text-dark-s)]">
                              {e.task_id === null ? (
                                <span className="font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                                  ⚠️ تسوية: {e.task_title}
                                </span>
                              ) : (
                                e.task_title
                              )}
                            </td>
                            {!isMember && !isSupervisor && <td className="px-3 py-3 text-[var(--text-gray)]">{e.team_name}</td>}
                            <td className="px-3 py-3 text-[var(--text-dark-h)]">{e.seconds_earned}</td>
                            <td className="px-3 py-3 text-[var(--text-dark-h)]">✖️{e.multiplier || 1}</td>
                            <td className={`px-3 py-3 font-bold ${e.total_earned_iqd < 0 ? 'text-red-650 dark:text-red-450' : 'text-green-600 dark:text-green-400'}`}>
                              {formatIQD(e.total_earned_iqd)}
                            </td>
                            <td className="px-3 py-3">
                              {e.is_paid ? (
                                <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">مدفوع ✅</span>
                              ) : (
                                <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300">غير مدفوع</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Payments Tab */}
      {(isMember || isTeamLeader) && !isSupervisor && tab === 'payments' && showFinancials && (
        <div className="space-y-6">
          {/* For Team Leader: Received Payments from Owner */}
          {isTeamLeader && (
            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-sm">
              <div className="px-4 py-3 bg-[var(--bg-flat-50)] border-b border-[var(--border-light)] flex items-center gap-2">
                <Wallet className="w-4 h-4 text-green-600 dark:text-green-400" />
                <h3 className="text-sm font-bold text-[var(--text-dark-h)]">💰 الدفعات المستلمة من الإدارة (المالك)</h3>
              </div>
              {loadingReceived ? (
                <div className="text-center py-6 text-[var(--text-muted)]">جاري التحميل...</div>
              ) : receivedPayments.length === 0 ? (
                <div className="text-center py-6 text-[var(--text-muted)]">لا توجد دفعات مستلمة من الإدارة بعد</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[var(--bg-flat-50)]">
                        <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">تاريخ الدفع</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المبلغ المستلم</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">البيان والملاحظات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receivedPayments.map(p => (
                        <tr key={p.id} className="border-t border-[var(--border-light)] hover:bg-[var(--table-row-hover)] transition">
                          <td className="px-4 py-3.5 text-[var(--text-dark-h)]">{new Date(p.paid_at).toLocaleDateString('ar-IQ')}</td>
                          <td className="px-4 py-3.5 font-bold text-green-650 dark:text-green-450">{formatIQD(p.amount_iqd)}</td>
                          <td className="px-4 py-3.5 text-[var(--text-dark-s)]">{p.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Payments List (Received for Member, Paid to Members for Leader) */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-sm">
            <div className="px-4 py-3 bg-[var(--bg-flat-50)] border-b border-[var(--border-light)] flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <h3 className="text-sm font-bold text-[var(--text-dark-h)]">
                {isMember ? '💳 سجل الدفعات المستلمة من قائد الفريق' : '💳 الدفعات المصروفة لأعضاء الفريق'}
              </h3>
            </div>
            {loadingPayments ? (
              <div className="text-center py-12 text-[var(--text-muted)]">جاري التحميل...</div>
            ) : payments.length === 0 ? (
              <div className="text-center py-12 text-[var(--text-muted)]">
                {isMember ? 'لا توجد دفعات مستلمة بعد' : 'لا توجد دفعات مصروفة لأي عضو بعد'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[var(--bg-flat-50)]">
                      {!isMember && <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">العضو المستلم</th>}
                      <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">التاريخ</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المبلغ</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">ملاحظات الصرف</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(p => (
                      <tr key={p.id} className="border-t border-[var(--border-light)] hover:bg-[var(--table-row-hover)] transition">
                        {!isMember && <td className="px-4 py-3.5 font-bold text-[var(--text-dark-h)]">{p.employee_name}</td>}
                        <td className="px-4 py-3.5 text-[var(--text-dark-h)]">{new Date(p.paid_at).toLocaleDateString('ar-IQ')}</td>
                        <td className="px-4 py-3.5 font-bold text-green-600 dark:text-green-400">{formatIQD(p.amount_iqd)}</td>
                        <td className="px-4 py-3.5 text-[var(--text-dark-s)]">{p.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Deductions Tab */}
      {(isMember || isTeamLeader) && !isSupervisor && tab === 'deductions' && showFinancials && (
        <div className="space-y-6">
          {/* Personal Adjustments from Admin (For Leader/Member) */}
          {myPersonalAdjustments.length > 0 && (
            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-sm">
              <div className="px-4 py-3 bg-[var(--bg-flat-50)] border-b border-[var(--border-light)] flex items-center gap-2">
                <Wallet className="w-4 h-4 text-red-600 dark:text-red-400" />
                <h3 className="text-sm font-bold text-[var(--text-dark-h)]">⚠️ الخصومات والتسويات الشخصية من الإدارة (المالك)</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[var(--bg-flat-50)]">
                      <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">تاريخ التسوية</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">نوع القيد</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المبلغ</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">الملاحظات والسبب</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myPersonalAdjustments.map(adj => (
                      <tr key={adj.id} className="border-t border-[var(--border-light)] hover:bg-[var(--table-row-hover)] transition">
                        <td className="px-4 py-3.5 text-[var(--text-dark-h)]">
                          {new Date(adj.created_at).toLocaleDateString('ar-IQ')}
                        </td>
                        <td className="px-4 py-3.5">
                          <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${adj.total_earned_iqd < 0 ? 'bg-red-100 text-red-700 dark:bg-red-900/30' : 'bg-green-100 text-green-700 dark:bg-green-900/30'}`}>
                            {adj.total_earned_iqd < 0 ? '⛔ خصم مالي' : '🎁 مكافأة / تسوية إضافية'}
                          </span>
                        </td>
                        <td className={`px-4 py-3.5 font-bold ${adj.total_earned_iqd < 0 ? 'text-red-650 dark:text-red-450' : 'text-green-650 dark:text-green-450'}`}>
                          {adj.total_earned_iqd < 0 ? '-' : '+'}{formatIQD(Math.abs(adj.total_earned_iqd))}
                        </td>
                        <td className="px-4 py-3.5 text-[var(--text-dark-s)]">{adj.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Regular Deductions List */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-sm">
            <div className="px-4 py-3 bg-[var(--bg-flat-50)] border-b border-[var(--border-light)] flex items-center gap-2">
              <Filter className="w-4 h-4 text-red-600 dark:text-red-400" />
              <h3 className="text-sm font-bold text-[var(--text-dark-h)]">
                {isTeamLeader ? '⛔ سجل خصومات أعضاء الفريق' : '⛔ خصومات الغياب والتأخير'}
              </h3>
            </div>
            {loadingDeductions ? (
              <div className="text-center py-12 text-[var(--text-muted)]">جاري التحميل...</div>
            ) : deductions.length === 0 ? (
              <div className="text-center py-12 text-[var(--text-muted)]">🎉 لا توجد خصومات مسجلة</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[var(--bg-flat-50)]">
                      {!isMember && <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">الموظف</th>}
                      <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">التاريخ</th>
                      <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">النوع</th>
                      <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المبلغ</th>
                      <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">السبب</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deductions.map(d => {
                      const typeLabels = { late: '⏰ تأخير', quality: '⚠️ جودة', absence: '🚫 غياب', manual: '📋 يدوي' };
                      return (
                        <tr key={d.id} className="border-t border-[var(--border-light)] hover:bg-[var(--table-row-hover)] transition">
                          {!isMember && <td className="px-3 py-3 font-bold text-[var(--text-dark-h)]">{d.employee_name}</td>}
                          <td className="px-3 py-3 text-[var(--text-dark-h)]">{new Date(d.created_at).toLocaleDateString('ar-IQ')}</td>
                          <td className="px-3 py-3">
                            <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                              {typeLabels[d.deduction_type] || d.deduction_type}
                            </span>
                          </td>
                          <td className="px-3 py-3 font-bold text-red-600 dark:text-red-400">-{formatIQD(d.amount_iqd)}</td>
                          <td className="px-3 py-3 text-[var(--text-gray)]">{d.reason || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ Seconds Tab ═══ */}
      {(isMember || isTeamLeader) && !isSupervisor && tab === 'seconds' && (
        <div className="space-y-4">
          {/* Day Filter */}
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm font-bold text-[var(--text-gray)]">فلتر حسب يوم:</label>
            <input type="date" value={dayFilter} onChange={e => setDayFilter(e.target.value)}
              className="p-2 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm" />
            {dayFilter && (
              <button onClick={() => setDayFilter('')}
                className="px-3 py-1.5 rounded-lg text-xs font-bold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:opacity-80 transition">
                ✕ عرض الشهر كامل
              </button>
            )}
          </div>

          {loadingSeconds ? (
            <div className="text-center py-12 text-[var(--text-muted)]">جاري التحميل...</div>
          ) : !secondsSummary ? (
            <div className="text-center py-12 text-[var(--text-muted)]">لا توجد بيانات</div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className={`grid ${isTeamLeader ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-3'} gap-4`}>
                <div className="p-4 rounded-2xl bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400">
                  <p className="text-xs font-bold opacity-60 mb-1">{dayFilter ? 'ثواني اليوم المحدد' : 'إجمالي ثواني الشهر'}</p>
                  <p className="text-2xl font-bold">{formatSeconds(secondsSummary.total_seconds)}</p>
                  <p className="text-xs opacity-50 mt-1">{secondsSummary.total_seconds} ثانية</p>
                </div>
                <div className="p-4 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
                  <p className="text-xs font-bold opacity-60 mb-1">عدد المهام المقبولة</p>
                  <p className="text-2xl font-bold">{secondsSummary.task_count}</p>
                </div>
                {!dayFilter && (
                  <div className="p-4 rounded-2xl bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400">
                    <p className="text-xs font-bold opacity-60 mb-1">ثواني اليوم</p>
                    <p className="text-2xl font-bold">{formatSeconds(secondsSummary.today_seconds || 0)}</p>
                    <p className="text-xs opacity-50 mt-1">{secondsSummary.today_seconds || 0} ثانية</p>
                  </div>
                )}
              </div>

              {/* Team Leader: By Employee Table */}
              {isTeamLeader && secondsSummary.by_employee && secondsSummary.by_employee.length > 0 && (
                <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden">
                  <div className="px-4 py-3 bg-[var(--bg-flat-50)] border-b border-[var(--border-light)]">
                    <h3 className="text-sm font-bold text-[var(--text-dark-h)]">👥 ثواني كل عضو</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[var(--bg-flat-50)]">
                          <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">#</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">الموظف</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">الدور</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">الثواني</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المدة</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المهام</th>
                        </tr>
                      </thead>
                      <tbody>
                        {secondsSummary.by_employee.map((emp, i) => (
                          <tr key={emp.employee_id} className="border-t border-[var(--border-light)] hover:bg-[var(--table-row-hover)] transition">
                            <td className="px-4 py-3 text-[var(--text-gray)]">
                              {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                            </td>
                            <td className="px-4 py-3 font-bold text-[var(--text-dark-h)]">{emp.employee_name}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${emp.role_type === 'team_leader' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`}>
                                {emp.role_type === 'team_leader' ? 'قائد' : 'عضو'}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-bold text-cyan-600 dark:text-cyan-400">{emp.total_seconds}</td>
                            <td className="px-4 py-3 text-[var(--text-dark-s)]">{formatSeconds(emp.total_seconds)}</td>
                            <td className="px-4 py-3 text-[var(--text-gray)]">{emp.task_count} مهمة</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Details Table */}
              {secondsSummary.details && secondsSummary.details.length > 0 && (
                <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden">
                  <div className="px-4 py-3 bg-[var(--bg-flat-50)] border-b border-[var(--border-light)]">
                    <h3 className="text-sm font-bold text-[var(--text-dark-h)]">📋 تفاصيل المهام</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[var(--bg-flat-50)]">
                          {isTeamLeader && <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">الموظف</th>}
                          <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المهمة</th>
                          <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">الثواني</th>
                          <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المعامل</th>
                          <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">الفريق</th>
                          <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">التاريخ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {secondsSummary.details.map((d, i) => (
                          <tr key={i} className="border-t border-[var(--border-light)] hover:bg-[var(--table-row-hover)] transition">
                            {isTeamLeader && <td className="px-3 py-3 font-bold text-[var(--text-dark-h)]">{d.employee_name}</td>}
                            <td className="px-3 py-3 text-[var(--text-dark-s)]">{d.task_title}</td>
                            <td className="px-3 py-3 font-bold text-cyan-600 dark:text-cyan-400">{d.seconds_earned} ث</td>
                            <td className="px-3 py-3 text-[var(--text-dark-h)]">✖️{d.multiplier || 1}</td>
                            <td className="px-3 py-3 text-[var(--text-gray)]">{d.team_name}</td>
                            <td className="px-3 py-3 text-[var(--text-gray)] text-xs">{d.created_at ? new Date(d.created_at).toLocaleDateString('ar-IQ') : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {secondsSummary.details && secondsSummary.details.length === 0 && (
                <div className="text-center py-12 text-[var(--text-muted)]">لا توجد ثواني مُنجزة {dayFilter ? 'في هذا اليوم' : 'هذا الشهر'}</div>
              )}
            </>
          )}
        </div>
      )}

      {/* ═══ Debts Tab ═══ */}
      {(isMember || isTeamLeader) && !isSupervisor && tab === 'debts' && showFinancials && (
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden">
          {loadingDebts ? (
            <div className="text-center py-12 text-[var(--text-muted)]">جاري التحميل...</div>
          ) : myDebts.length === 0 ? (
            <div className="text-center py-12 text-[var(--text-muted)]">لا توجد ديون أو سلف مسجلة باسمك</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[var(--bg-flat-50)]">
                    <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">تاريخ الدين</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">النوع</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">ملاحظات</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المبلغ الكلي</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المسدد</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-[var(--text-gray)]">المتبقي</th>
                  </tr>
                </thead>
                <tbody>
                  {myDebts.map(d => (
                    <tr key={d.id} className="border-t border-[var(--border-light)] hover:bg-[var(--table-row-hover)] transition">
                      <td className="px-3 py-3 text-[var(--text-dark-h)]">{new Date(d.created_at).toLocaleDateString('ar-IQ')}</td>
                      <td className="px-3 py-3 text-[var(--text-dark-s)]">
                        {d.debt_type === 'cash_advance' ? 'سلفة نقدية' : d.debt_type === 'subscription_fee' ? 'رسوم اشتراك' : 'أخرى'}
                      </td>
                      <td className="px-3 py-3 text-[var(--text-gray)]">{d.notes || '—'}</td>
                      <td className="px-3 py-3 font-bold">{d.amount} {d.currency === 'USD' ? '$' : 'ع.د'}</td>
                      <td className="px-3 py-3 font-bold text-green-600 dark:text-green-400">{d.total_paid} {d.currency === 'USD' ? '$' : 'ع.د'}</td>
                      <td className="px-3 py-3 font-bold text-red-600 dark:text-red-400">{d.remaining} {d.currency === 'USD' ? '$' : 'ع.د'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ═══ MODAL: Add Adjustment/Deduction ═══ */}
      {showAdjustModal && (
        <Modal title="إضافة تسوية مالية / خصم ➕" onClose={() => setShowAdjustModal(false)}>
          <form onSubmit={handleAdjustSubmit} className="space-y-4 text-right">
            <div>
              <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">الموظف / قائد الفريق المستهدف</label>
              {loadingEmployees ? (
                <p className="text-xs text-[var(--text-muted)]">جاري تحميل الموظفين...</p>
              ) : (
                <select
                  value={adjustEmpId}
                  onChange={e => setAdjustEmpId(e.target.value)}
                  required
                  className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 transition"
                >
                  <option value="">اختر الموظف...</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name} ({emp.role === 'team_leader' ? 'قائد فريق' : emp.role === 'team_member' ? 'عضو فريق' : emp.role})
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">نوع العملية</label>
                <select
                  value={adjustType}
                  onChange={e => setAdjustType(e.target.value)}
                  className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 transition"
                >
                  <option value="deduction">خصم مالي 🔻 (سالب)</option>
                  <option value="bonus">تعويض / مكافأة 🟢 (موجب)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">المبلغ (IQD)</label>
                <input
                  type="number"
                  placeholder="مثال: 25000"
                  value={adjustAmount}
                  onChange={e => setAdjustAmount(e.target.value)}
                  required
                  className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 transition"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">الشهر المستهدف</label>
                <input
                  type="month"
                  value={adjustMonth}
                  onChange={e => setAdjustMonth(e.target.value)}
                  required
                  className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">السبب / الملاحظات (تظهر للموظف)</label>
              <textarea
                rows="3"
                placeholder="مثال: خصم 50,000 بسبب تأخير تسليم تسليمات العميل X لأكثر من يومين..."
                value={adjustNotes}
                onChange={e => setAdjustNotes(e.target.value)}
                required
                className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 transition"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowAdjustModal(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-bold transition"
              >
                إلغاء
              </button>
              <button
                type="submit"
                disabled={submittingAdjust}
                className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-bold transition disabled:opacity-50"
              >
                {submittingAdjust ? 'جاري الحفظ...' : 'حفظ التسوية'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function SummaryCard({ title, value, color, icon }) {
  const colors = {
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
  };

  return (
    <div className={`p-4 rounded-2xl ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-2 opacity-60">{icon}<span className="text-xs font-bold">{title}</span></div>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
}
